/**
 * src/services/antigravity-bridge.ts
 * -----------------------------------
 * Web IDE Client Bridge for Antigravity Remote Coding.
 * Connects https://avi.kaywat.codes directly to the running Antigravity IDE (Desktop CDP)
 * or Proxmox Worker VMs with Credential Hub Account Pool rotation.
 */

import antigravityManifest from './antigravity-manifest.json';
import antigravityContentCache from './antigravity-content-cache.json';

export interface AntigravityTarget {
  id: string;
  name: string;
  type: string;
  vmid?: number;
  ip?: string;
  host?: string;
  port?: number;
  online?: boolean;
}

export interface AntigravityServer {
  id: string;
  nickname: string;
  endpoint: string;
  type: 'workstation' | 'proxmox_vm' | 'vps' | 'cluster' | 'custom';
  status: 'online' | 'processing' | 'starting' | 'stopping' | 'offline';
  vmid?: number;
  lastPing?: number;
  pat?: string;
  isDefault?: boolean;
}

export interface AntigravityStatus {
  status: string;
  activeTarget: AntigravityTarget;
  nodes: any[];
  pool: {
    total: number;
    readyCount: number;
    inUseCount: number;
    exhaustedCount: number;
    failedCount: number;
    activeAccount?: { email: string };
    exhausted: any[];
    ready: any[];
  };
}

const DEFAULT_SERVERS: AntigravityServer[] = [];

class AntigravityWebBridge {
  private customBaseUrl: string | null = null;
  private servers: AntigravityServer[] = [];
  private userPat: string = '';

  constructor() {
    this.customBaseUrl = localStorage.getItem('antigravity_bridge_url') || null;
    this.loadServers();
    this.getUserPat();
    // Auto-probe local workstation if no servers linked yet
    if (this.servers.length === 0) {
      setTimeout(() => this.autoDetectLocalNode(), 300);
    }
  }

  /**
   * Load Antigravity servers from localStorage (only user-configured or PAT-authenticated nodes)
   */
  loadServers(): AntigravityServer[] {
    try {
      const raw = localStorage.getItem('avi_antigravity_servers');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.servers = parsed.filter(s => s && s.id && typeof s.id === 'string');
          return this.servers;
        }
      }
    } catch (e) {
      console.warn('[AntigravityBridge] Failed to load servers from storage:', e);
    }

    // Default to dev desktop-2 (This PC) so the user is immediately connected
    const pat = this.getUserPat();
    this.servers = [
      {
        id: 'desktop2',
        nickname: 'dev desktop-2 (THIS PC)',
        endpoint: 'http://127.0.0.1:5065',
        type: 'workstation',
        status: 'online',
        pat: pat,
        lastPing: Date.now(),
        isDefault: false,
      }
    ];
    this.saveServers();
    return this.servers;
  }

  /**
   * Ensure standard target server exists in servers list
   */
  ensureServerForTarget(targetId: string = 'desktop2'): AntigravityServer {
    const pat = this.getUserPat();
    const t = targetId.toLowerCase().replace(/[-_]/g, '');
    let existing = this.servers.find(s => s.id === targetId || s.id.toLowerCase().replace(/[-_]/g, '') === t);
    if (!existing) {
      if (t === 'desktop2' || t === 'agdesktop2') {
        existing = {
          id: 'desktop2',
          nickname: 'dev desktop-2 (THIS PC)',
          endpoint: 'http://127.0.0.1:5065',
          type: 'workstation',
          status: 'online',
          pat: pat,
          lastPing: Date.now(),
          isDefault: false
        };
        this.servers.unshift(existing);
        this.saveServers();
      } else if (t === 'desktop1' || t === 'agdesktop1') {
        existing = {
          id: 'desktop1',
          nickname: 'dev desktop-1 (DESKTOP)',
          endpoint: 'http://100.114.208.0:5065',
          type: 'workstation',
          status: 'online',
          pat: pat,
          lastPing: Date.now(),
          isDefault: false
        };
        this.servers.push(existing);
        this.saveServers();
      } else if (t === 'bbvps') {
        existing = {
          id: 'bb-vps',
          nickname: 'bb-vps (betterbase)',
          endpoint: 'https://betterbase-api.kaywat.codes',
          type: 'vps',
          status: 'online',
          pat: pat,
          lastPing: Date.now(),
          isDefault: false
        };
        this.servers.push(existing);
        this.saveServers();
      }
    }
    return existing || this.servers[0];
  }

  /**
   * Save servers to localStorage
   */
  saveServers(servers?: AntigravityServer[]) {
    if (servers) this.servers = servers;
    try {
      localStorage.setItem('avi_antigravity_servers', JSON.stringify(this.servers));
    } catch (e) {
      console.warn('[AntigravityBridge] Failed to save servers:', e);
    }
  }

  /**
   * Returns all Antigravity servers
   */
  getServers(): AntigravityServer[] {
    if (!this.servers || this.servers.length === 0) {
      return this.loadServers();
    }
    return this.servers;
  }

  /**
   * Add a new Antigravity server
   */
  addServer(data: { nickname: string; endpoint: string; type?: AntigravityServer['type']; pat?: string }): AntigravityServer {
    const id = 'ag_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 6);
    const newServer: AntigravityServer = {
      id,
      nickname: data.nickname.trim(),
      endpoint: data.endpoint.trim().replace(/\/$/, ''),
      type: data.type || 'custom',
      status: 'starting',
      pat: data.pat?.trim() || this.getUserPat(),
      lastPing: Date.now(),
      isDefault: false,
    };
    this.servers.push(newServer);
    this.saveServers();
    this.pingServer(newServer);
    return newServer;
  }

  /**
   * Delete an Antigravity server
   */
  deleteServer(id: string): boolean {
    const idx = this.servers.findIndex(s => s.id === id);
    if (idx !== -1) {
      this.servers.splice(idx, 1);
      this.saveServers();
      return true;
    }
    return false;
  }

  /**
   * Automatically probes localhost port 5065 for a running Antigravity Extension daemon.
   * If detected and matching user's PAT, auto-registers this workstation in the server list!
   */
  async autoDetectLocalNode(): Promise<AntigravityServer | null> {
    const pat = this.getUserPat();
    const candidateEndpoints = [
      'http://127.0.0.1:5065',
      'http://localhost:5065',
      'http://100.101.196.68:5065', // dev desktop-2 (Tailscale)
      'http://192.168.12.214:5065',  // dev desktop-2 (LAN)
      'http://100.114.208.0:5065'    // dev desktop-1 (Tailscale)
    ];

    for (const endpoint of candidateEndpoints) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1800);
        const res = await fetch(`${endpoint}/api/antigravity/status`, {
          headers: {
            'Content-Type': 'application/json',
            'x-avi-pat': pat,
          },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          const nickname = data.nickname || data.name || 'Antigravity IDE (This PC)';
          const existing = this.servers.find(s => s.endpoint === endpoint || s.id === 'local_workstation');

          let targetServer: AntigravityServer;
          if (existing) {
            existing.status = 'online';
            existing.nickname = nickname;
            existing.lastPing = Date.now();
            this.saveServers();
            targetServer = existing;
          } else {
            targetServer = {
              id: 'local_workstation',
              nickname,
              endpoint,
              type: 'workstation',
              status: 'online',
              pat,
              lastPing: Date.now(),
              isDefault: false,
            };
            this.servers.unshift(targetServer);
            this.saveServers();
          }

          localStorage.setItem('avi_antigravity_target', targetServer.id);
          localStorage.setItem('avi_antigravity_mode', 'true');

          const ide = (window as any).ideEngine;
          if (ide) {
            ide.antigravityServers = this.servers;
            ide.activeAntigravityServerId = targetServer.id;
            ide.renderAntigravityServersList();
            this.syncToIdeEngine(ide, targetServer.id);
          }
          return targetServer;
        }
      } catch {
        // Continue probing candidates
      }
    }
    return null;
  }

  /**
   * Retrieves or generates the user's Personal Access Token (PAT) tied to their member account
   */
  getUserPat(): string {
    const currentUser = (window as any).currentUser;
    const accountEmail = currentUser?.email || 'info@kaywat.me';

    let pat = localStorage.getItem('avi_user_pat');
    if (!pat) {
      try {
        const emailSlug = btoa(accountEmail).replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
        const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
        pat = `avi_pat_${emailSlug}_${randomHex}`;
      } catch {
        pat = `avi_pat_member_${Date.now().toString(36)}`;
      }
      localStorage.setItem('avi_user_pat', pat);
    }
    this.userPat = pat;
    return pat;
  }

  /**
   * Regenerates a new Personal Access Token (PAT)
   */
  regenerateUserPat(): string {
    const currentUser = (window as any).currentUser;
    const accountEmail = currentUser?.email || 'info@kaywat.me';
    let emailSlug = 'member';
    try {
      emailSlug = btoa(accountEmail).replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
    } catch {}
    const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    const newPat = `avi_pat_${emailSlug}_${randomHex}`;
    localStorage.setItem('avi_user_pat', newPat);
    this.userPat = newPat;
    return newPat;
  }

  /**
   * Pings a specific Antigravity server to detect online, processing, starting, or offline status
   */
  async pingServer(server: AntigravityServer): Promise<AntigravityServer['status']> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2500);
      const res = await fetch(`${server.endpoint}/api/antigravity/status`, {
        headers: {
          'Content-Type': 'application/json',
          'x-avi-pat': server.pat || this.getUserPat(),
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        let newStatus: AntigravityServer['status'] = 'online';
        if (data.isGenerating || data.processing || data.status === 'processing' || data.status === 'generating') {
          newStatus = 'processing';
        } else if (data.status === 'starting' || data.status === 'launching') {
          newStatus = 'starting';
        } else if (data.status === 'stopping') {
          newStatus = 'stopping';
        } else {
          newStatus = 'online';
        }
        server.status = newStatus;
        server.lastPing = Date.now();
        this.saveServers();
        return newStatus;
      } else {
        server.status = 'offline';
        server.lastPing = Date.now();
        this.saveServers();
        return 'offline';
      }
    } catch (e) {
      server.status = 'offline';
      server.lastPing = Date.now();
      this.saveServers();
      return 'offline';
    }
  }

  /**
   * Concurrently pings all configured Antigravity servers
   */
  async pingAllServers(): Promise<AntigravityServer[]> {
    const promises = this.servers.map(s => this.pingServer(s));
    await Promise.allSettled(promises);
    return this.servers;
  }

  /**
   * Set custom bridge API endpoint
   */
  setBaseUrl(url: string) {
    this.customBaseUrl = url.replace(/\/$/, '');
    if (this.customBaseUrl) {
      localStorage.setItem('antigravity_bridge_url', this.customBaseUrl);
    } else {
      localStorage.removeItem('antigravity_bridge_url');
    }
  }

  getBaseUrl(targetId?: string): string {
    if (this.customBaseUrl) return this.customBaseUrl;
    const targetKey = targetId || localStorage.getItem('avi_antigravity_target') || 'desktop2';

    // When running in browser over HTTPS, route through public HTTPS proxy to eliminate Mixed Content restrictions
    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
      return 'https://betterbase-api.kaywat.codes';
    }

    // Check if target matches one of the user's servers by ID or nickname
    const foundServer = this.servers.find(s => s.id === targetKey || s.id.toLowerCase() === targetKey.toLowerCase());
    if (foundServer && foundServer.endpoint) {
      return foundServer.endpoint.replace(/\/$/, '');
    }

    const t = targetKey.toLowerCase().replace(/[-_]/g, '');

    // dev desktop-2 (Current local workstation)
    if (t === 'desktop2' || t === 'agdesktop2') {
      return 'http://127.0.0.1:5065';
    }
    // dev desktop-1 (Secondary remote workstation)
    if (t === 'desktop1' || t === 'agdesktop1') {
      return 'http://100.114.208.0:5065';
    }
    // BetterBase VPS
    if (t === 'bbvps') {
      return 'https://betterbase-api.kaywat.codes';
    }
    // Render
    if (t === 'render') {
      return 'https://avi.kaywat.codes';
    }
    // Proxmox Worker Cluster
    return 'http://10.0.0.208:5065';
  }

  /**
   * Check if user enabled Antigravity bridge mode in the Web IDE
   */
  isModeActive(): boolean {
    return localStorage.getItem('avi_antigravity_mode') === 'true';
  }

  /**
   * Toggle Antigravity bridge mode on/off
   */
  setModeActive(active: boolean) {
    localStorage.setItem('avi_antigravity_mode', active ? 'true' : 'false');
  }

  /**
   * Fetch live status of bridge, targets, and account pool
   */
  async getStatus(targetId?: string): Promise<AntigravityStatus | null> {
    const url = this.getBaseUrl(targetId);
    try {
      const res = await fetch(`${url}/api/antigravity/status`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn('[AntigravityBridge] Status check failed on ' + url + ':', err);
      return null;
    }
  }

  /**
   * Cycle target device (Desktop -> VM1 -> VM2 -> VM3 -> VM4 -> All -> Desktop)
   */
  async cycleTarget(): Promise<any> {
    const url = this.getBaseUrl();
    const res = await fetch(`${url}/api/antigravity/tab`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  }

  /**
   * Dispatch prompt/coding instruction to Antigravity engine
   */
  async dispatch(prompt: string, targetId?: string): Promise<any> {
    const target = targetId || localStorage.getItem('avi_antigravity_target') || 'desktop2';
    const url = this.getBaseUrl(target);
    const res = await fetch(`${url}/api/antigravity/dispatch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, targetId: target }),
    });
    return res.json();
  }

  /**
   * List files in the active Antigravity workspace
   * Probes live endpoints; each device returns its own unique workspace files.
   */
  async getWorkspaceFiles(targetId?: string): Promise<Array<{ name: string; relativePath: string; sizeBytes: number; folder?: string }>> {
    const target = targetId || localStorage.getItem('avi_antigravity_target') || 'desktop2';
    const pat = this.getUserPat();
    this.ensureServerForTarget(target);

    const isHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const candidateEndpoints: string[] = [];

    // Over HTTPS in browser, route via secure proxy first to prevent Mixed Content blocking
    if (isHttps) {
      candidateEndpoints.push(`https://betterbase-api.kaywat.codes/api/antigravity/files?target=${encodeURIComponent(target)}`);
    }

    const foundServer = this.servers.find(s => s.id === target || s.id.toLowerCase().replace(/[-_]/g, '') === target.toLowerCase().replace(/[-_]/g, ''));
    if (foundServer && foundServer.endpoint && !isHttps) {
      candidateEndpoints.push(`${foundServer.endpoint.replace(/\/$/, '')}/api/antigravity/files`);
    }

    const t = target.toLowerCase().replace(/[-_]/g, '');
    if (!isHttps) {
      if (t === 'desktop2' || t === 'agdesktop2') {
        candidateEndpoints.push('http://127.0.0.1:5065/api/antigravity/files');
        candidateEndpoints.push('http://localhost:5065/api/antigravity/files');
        candidateEndpoints.push('http://100.101.196.68:5065/api/antigravity/files');
        candidateEndpoints.push('http://192.168.12.214:5065/api/antigravity/files');
      } else if (t === 'desktop1' || t === 'agdesktop1') {
        candidateEndpoints.push('http://100.114.208.0:5065/api/antigravity/files');
      } else if (t === 'bbvps') {
        candidateEndpoints.push('https://betterbase-api.kaywat.codes/api/antigravity/files');
      }
    }

    // Attempt live fetch across candidate endpoints
    for (const url of candidateEndpoints) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'x-avi-pat': (foundServer && foundServer.pat) || pat,
          },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (res.ok) {
          const data = await res.json();
          if (data && data.files && Array.isArray(data.files) && data.files.length > 0) {
            if (foundServer) {
              foundServer.status = 'online';
              foundServer.lastPing = Date.now();
              this.saveServers();
            }
            return data.files;
          }
        }
      } catch (e) {
        // Try next candidate
      }
    }

    // Do NOT return fake 18-file static cache: each device must show its own real files
    return [];
  }

  /**
   * Fetch specific file content from the Antigravity workspace
   */
  async getFileContent(relativePath: string, targetId?: string): Promise<string | null> {
    const target = targetId || localStorage.getItem('avi_antigravity_target') || 'desktop2';
    const pat = this.getUserPat();
    const isHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';

    let url = `${this.getBaseUrl(target)}/api/antigravity/file?path=${encodeURIComponent(relativePath)}`;
    if (isHttps) {
      url += `&target=${encodeURIComponent(target)}`;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-avi-pat': pat,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        if (data && data.content) return data.content;
      }
    } catch {
      // Ignore network block
    }

    return null;
  }

  /**
   * Synchronize workspace files directly into the Web IDE file tree
   */
  async syncToIdeEngine(ideEngine: any, targetId?: string): Promise<number> {
    if (!ideEngine) return 0;
    const target = targetId || localStorage.getItem('avi_antigravity_target') || 'desktop2';
    this.ensureServerForTarget(target);

    const files = await this.getWorkspaceFiles(target);
    ideEngine.files.clear();

    if (!files || files.length === 0) {
      ideEngine.renderExplorer();
      return 0;
    }

    files.forEach(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      let lang = 'plaintext';
      if (ext === 'ts' || ext === 'js') lang = 'javascript';
      else if (ext === 'html') lang = 'html';
      else if (ext === 'css') lang = 'css';
      else if (ext === 'json') lang = 'json';
      else if (ext === 'md') lang = 'markdown';
      else if (ext === 'py') lang = 'python';

      const pathKey = f.relativePath || f.name;

      ideEngine.files.set(pathKey, {
        name: f.name,
        path: pathKey,
        content: '',
        language: lang,
        sizeBytes: f.sizeBytes,
        folder: f.folder
      });
    });

    // Make sure the explorer view shows the workspace
    const workspaceContent = document.getElementById('ideWorkspaceContent');
    const menuContent = document.getElementById('ideMenuContent');
    if (workspaceContent) workspaceContent.style.display = 'flex';
    if (menuContent) menuContent.style.display = 'none';

    // Set first important file as active (e.g. auth.env or main file)
    const preferred = files.find(f => f.name === 'auth.env') ||
                      files.find(f => f.name === 'app.ts') ||
                      files.find(f => f.name === 'index.html') ||
                      files[0];
    if (preferred && !ideEngine.activeFile) {
      ideEngine.activeFile = preferred.relativePath || preferred.name;
      this.getFileContent(ideEngine.activeFile, target).then(content => {
        if (content !== null) {
          const entry = ideEngine.files.get(ideEngine.activeFile);
          if (entry) entry.content = content;
          if (ideEngine.currentView === 'code') ideEngine.renderCodeViewer(ideEngine.activeFile);
        }
      });
    }

    ideEngine.renderExplorer();
    if (typeof ideEngine.loadAntigravityServers === 'function') ideEngine.loadAntigravityServers();
    if (typeof ideEngine.renderAntigravityServersList === 'function') ideEngine.renderAntigravityServersList();

    return files.length;
  }

  /**
   * Get URL for live screenshot of the IDE workbench
   */
  getScreenshotUrl(targetId?: string): string {
    const url = this.getBaseUrl(targetId);
    return `${url}/api/antigravity/screenshot?t=${Date.now()}`;
  }
}

export const antigravityBridge = new AntigravityWebBridge();
(window as any).antigravityBridge = antigravityBridge;
